import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Loader2, MessageSquare, MessageCircle, CheckCheck, XCircle, Clock, ExternalLink } from 'lucide-react';
import API_URL from '@/config/api';

/**
 * Collapsible "Recent messages" panel for an installer card. Fetches lazily —
 * only hits the backend the first time the user expands the section.
 * Includes a "View full thread" button that opens the parent's thread modal.
 */
export default function InstallerRecentMessages({ installerId, refreshKey, onViewThread, unreadCount = 0 }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/messages/history?recipient_ref=${encodeURIComponent(installerId)}&limit=5`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (_e) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey]);

  const statusIcon = (status) => {
    if (status === 'delivered' || status === 'read') return <CheckCheck className="w-3 h-3 text-emerald-600" />;
    if (status === 'failed' || status === 'declined' || status === 'undelivered') return <XCircle className="w-3 h-3 text-red-500" />;
    return <Clock className="w-3 h-3 text-gray-400" />;
  };

  const channelIcon = (channel) => (
    channel === 'superchat_whatsapp'
      ? <MessageCircle className="w-3 h-3 text-emerald-600" />
      : <MessageSquare className="w-3 h-3 text-blue-600" />
  );

  return (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`installer-history-${installerId}`}>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-gray-800"
        >
          <span>Recent messages</span>
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {onViewThread && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewThread(); }}
            className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
            data-testid={`installer-thread-${installerId}`}
            title={unreadCount > 0 ? `${unreadCount} unread ${unreadCount === 1 ? 'reply' : 'replies'} — click to view` : 'Open full conversation'}
          >
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold leading-none animate-pulse"
                data-testid={`installer-unread-${installerId}`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            Thread <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-1.5">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : (messages || []).length === 0 ? (
            <p className="text-[11px] text-gray-400 italic">No messages sent yet.</p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id || `${m.provider_id}-${m.created_at}`}
                className="text-[11px] rounded-md bg-gray-50 border border-gray-200 px-2 py-1.5"
                data-testid="history-message-row"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-gray-600 truncate">
                    {channelIcon(m.channel)}
                    <span className="font-semibold">{m.channel?.includes('whatsapp') ? 'WhatsApp' : 'SMS'}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500 truncate">{new Date(m.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
                    {statusIcon(m.status)} {m.status}
                  </span>
                </div>
                {(m.body || m.template_id) && (
                  <p className="mt-0.5 text-gray-700 line-clamp-2 whitespace-pre-wrap">
                    {m.body || `Template: ${m.template_id}`}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
